import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Box, Divider, Tab, Tabs } from '@mui/material';
import FhirGeneralInformation from '../components/general/FhirGeneralInformation';
import FhirOrganizationBanner from './FhirOrganizationBanner';
import { getUUID } from 'src/utils/url';
import { ChildList } from '../components/child/ChildList';
import LoadingScreen from 'src/components/LoadingScreen';
import { PatientList } from '../components/patient/PatientList';
import useTranslations from 'src/careflow/ph-core/internationalization/hook/useTranslations';
import { useOrganizations } from 'src/careflow/ph-core/fhir-client/fhir-react';
import { OrganizationWrapper } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/Organization';
import { checkAclValidation } from 'src/careflow/ph-core/admin/permissions/util/permission.utils';
import crsAcls from 'src/careflow/ph-core/admin/permissions/util/crs/crsAcls';
import { getOrganizationsByIds } from 'src/careflow/ph-core/admin/organization/service/organizations';
import PageContainer from 'src/careflow/common/PageContainer';
import { AffiliateList } from '../components/affiliated/AffiliateList';
import { PATH_DASHBOARD } from 'src/routes/paths';

interface TabPanelProps {
  children?: React.ReactNode;
  dir?: string;
  index: number;
  value: number;
}

export function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tabpanel-${index}`}
      aria-labelledby={`tabpanel-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

export default function FhirOrganizationDetails() {
  const translate = useTranslations(`fhirOrganization`, 'translations');

  const { uuid = '' } = useParams();

  const [organizations, { update: updateOrg, refresh: refreshOrg, isLoading: isLoadingOrg }] =
    useOrganizations({
      filter: {
        _id: uuid,
        _include: 'Organization:partof',
        _revinclude: 'Organization:partof',
      },
      map: OrganizationWrapper,
      autofetch: !!uuid,
    });

  const [currentTab, setCurrentTab] = useState(0);
  const [isSystemOrg, setIsSystemOrg] = useState<string | null>(null);

  const isAllowedViewChildOrgs = checkAclValidation({
    acls: [crsAcls.ADMIN.FHIR_ORGANIZATIONS.CHILD_ORGANIZATIONS.VIEW],
  });
  const isAllowedViewAffiliatedOrgs = checkAclValidation({
    acls: [crsAcls.ADMIN.FHIR_ORGANIZATIONS.AFFILIATE_ORGANIZATIONS.VIEW],
  });

  const handleTabChange = (e: React.SyntheticEvent, selectedTab: number) => {
    setCurrentTab(selectedTab);
  };

  const resourceEntries = useMemo(() => {
    if (!organizations?.length) {
      return { main: null, partOf: null, childs: null };
    }

    const main = organizations.find((org) => org?.id === uuid) ?? null;

    const partOfUUID = main?.partOf?.reference ? getUUID(main.partOf.reference) : null;

    const partOf = partOfUUID ? organizations.find((org) => org?.id === partOfUUID) ?? null : null;

    const childs = organizations.filter((e) => e?.id !== uuid && e?.id !== partOfUUID);

    return { main, partOf, childs };
  }, [organizations, uuid]);

  const handleUpdate = async (data) => {
    await updateOrg(data);
    await refreshOrg();
  };

  async function loadOrganization() {
    if (!resourceEntries?.main) return;
    const oList = await getOrganizationsByIds([resourceEntries?.main?.id ?? '']);
    const isSystem = oList?.length > 0 && oList?.[0]?.fhirId === uuid;
    setIsSystemOrg(isSystem ? oList?.[0]?.uuid : null);
  }

  useEffect(() => {
    loadOrganization();
  }, [resourceEntries]);

  if (isLoadingOrg) return <LoadingScreen />;

  return (
    <PageContainer
      pageTitle={translate('details.title')}
      title={translate('details.title')}
      breadCrumbsLinks={[
        { name: `${translate('dashboard')}`, href: PATH_DASHBOARD.root },
        { name: `${translate('titleList')}`, href: PATH_DASHBOARD.fhirOrganization.list },
        { name: `${translate('details.title')}` },
      ]}
    >
      <FhirOrganizationBanner
        organization={organizations?.[0]}
        translate={translate}
        isSystemOrg={isSystemOrg}
      />

      <Tabs value={currentTab} onChange={handleTabChange} sx={{ marginTop: 2.5 }}>
        <Tab label={translate('details.tabs.general')} />
        {isAllowedViewChildOrgs && <Tab label={translate('details.tabs.child')} />}
        {isAllowedViewAffiliatedOrgs && <Tab label={translate('details.tabs.affiliate')} />}
        <Tab label={translate('details.tabs.patient')} />
      </Tabs>

      <Divider />

      <TabPanel value={currentTab} index={0}>
        <FhirGeneralInformation
          organization={resourceEntries}
          update={updateOrg}
          refresh={refreshOrg}
          translate={translate}
        />
      </TabPanel>

      {isAllowedViewChildOrgs && (
        <TabPanel value={currentTab} index={1}>
          <ChildList organization={resourceEntries} translate={translate} update={handleUpdate} />
        </TabPanel>
      )}

      {isAllowedViewAffiliatedOrgs && (
        <TabPanel value={currentTab} index={2}>
          <AffiliateList organization={resourceEntries} translate={translate} />
        </TabPanel>
      )}

      <TabPanel value={currentTab} index={3}>
        <PatientList organization={resourceEntries} translate={translate} />
      </TabPanel>
    </PageContainer>
  );
}
